import React from "react";
import s from "./index.module.css";
import classnames from "classnames";
import { useDrop } from "react-dnd";
import uuid from "uuid4";
import { createControl, ItemTypes } from "../../../DnDTypes";
import { defaultState } from "../../control-properties";
import Tooltip from "@material-ui/core/Tooltip";
import TextField from "@material-ui/core/TextField";
import { FormFieldChecks, errorMessagesFor } from "../../../FormFieldChecks";
import makeStyles from "@material-ui/core/styles/makeStyles";
import withStyles from "@material-ui/core/styles/withStyles";
import { useSelector } from "react-redux";

const CssTextField = withStyles((theme) => ({
  root: {
    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor:
        theme.palette.primary.main === "#fff200" && theme.palette.primary.main,
    },
    "&:hover .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
      borderColor:
        theme.palette.primary.main === "#fff200" && theme.palette.primary.main,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor:
        theme.palette.primary.main === "#fff200" && theme.palette.primary.main,
    },
    "& .MuiOutlinedInput-input": {
      color:
        theme.palette.primary.main === "#fff200" && theme.palette.primary.main,
    },
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input": {
      color:
        theme.palette.primary.main === "#fff200" && theme.palette.primary.main,
    },
    "& .MuiInputLabel-outlined": {
      color: theme.palette.primary.main === "#fff200" && "#c2b90c",
    },
    "& .MuiInputLabel-outlined.Mui-focused": {
      color:
        theme.palette.primary.main === "#fff200" && theme.palette.primary.main,
    },
    backgroundColor: theme.palette.primary.main !== "#fff200" && "white",
    border: theme.palette.primary.main !== "#fff200" && "1px solid lightgrey",
    borderRadius: "4px",
  },
}))(TextField);

const ItemSeparator = ({
  displayMode,
  visible = false,
  dragMode,
  rowIndex,
  index,
  canDrop,
  moveItemToIndex,
}) => {
  const [{ isOver }, drop] = useDrop({
    accept: ItemTypes.COMPONENT,
    drop: (item, rest) => {
      if (!displayMode) {
        const isClone = item.dragMode === "clone";
        const isMove = item.dragMode === "move";

        const targetRow = rowIndex;
        const targetIndex = index;
        const sourceRow = item.rowIndex;
        const sourceIndex = item.index;

        if (isClone) {
        } else if (isMove) {
          moveItemToIndex &&
            moveItemToIndex({
              item,
              targetRow,
              targetIndex,
              sourceRow,
              sourceIndex,
            });
        }
      }
    },
    collect: (monitor, props) => {
      return {
        isOver: monitor.isOver(),
      };
    },
  });

  // only in form edit mode
  if (dragMode === "move" || dragMode === "clone") {
    return (
      <Tooltip
        title={
          !displayMode
            ? "Przeciągnij tu element formularza aby zmienić jego pozycję"
            : ""
        }
      >
        <div
          className={classnames(s.separator, { [s.show]: isOver })}
          ref={drop}
        ></div>
      </Tooltip>
    );
  }

  return null;
};

const ItemRowSeparator = ({ displayMode, dragMode }) => {
  const [{ isOver }, dropItem] = useDrop({
    accept: ItemTypes.COMPONENT,
    drop: (item, rest) => {},
    collect: (monitor, props) => {
      return {
        isOver: monitor.isOver(),
      };
    },
  });

  // only in form edit mode
  if (dragMode === "move" || dragMode === "clone") {
    return (
      <Tooltip
        title={
          !displayMode
            ? "Przeciągnij tu element formularza aby zmienić jego pozycję"
            : ""
        }
      >
        <div
          className={classnames(s.separatorRow, { [s.show]: isOver })}
          ref={dropItem}
        />
      </Tooltip>
    );
  }

  return null;
};

const useStyles = makeStyles((theme) => ({
  hintText: {
    fontSize: theme.typography.body2.hintText,
    lineHeight: "1.4",
    margin: "-5px auto 5px",
    color:
      theme.palette.primary.main === "#fff200"
        ? theme.palette.primary.main
        : "#999",
  },
  aalertHighContrast: {
    color: theme.palette.text.primary,
    fontWeight: "bold",
    fontFamily: "helvetica",
    textDecoration: "underline",
    "&:hover": {
      color: theme.palette.text.primary,
    },
  },
}));

// model = null | 'fill'
const RowControl = ({
  displayMode,
  update,
  updateValues,
  onNoteUpdate,
  notes,
  markSelectedConfiguration,
  items = [],
  dragMode,
  selectedId,
  formCreatorMode,
  removeItem,
  moveItemToIndex,
  rowIndex,
  showDropMarkers,
  setIsDragging,
  rowValues,
  filledValuesDetails,
  filledFormMode,
  disabled,
  rowsLength,
  alertDisplaying,
  textColorFilling,
  blankedRequiredFields,
  blankedRequiredArrayValueFields,
  checkBlankedFields,
  incorrectFileTypeIDs,
  setIncorrectFileTypeIDs,
  edit,
}) => {
  const classes = useStyles();
  const globalTheme = useSelector((s) => s.globalTheme);
  const [, drop] = useDrop({
    accept: ItemTypes.COMPONENT,
    drop: (item, rest) => {
      if (!displayMode) {
        const isClone = item.dragMode === "clone";

        const targetRow = rowIndex;
        const sourceRow = item.rowIndex;
        const sourceIndex = item.index;

        if (isClone && update) {
          update([
            ...items,
            {
              id: uuid(),
              dataType: item.subtype,
              configuration: {
                ...defaultState,
                label: item.defaultLabel || "",
              },
              // componentType: TODO in case we have multiple possible renderers
              // this field could be used to specify which one we want to use
            },
          ]);
        } else {
          if (!(targetRow < items.length)) {
            const targetIndex = items.findIndex((i) => item.id === i.id);
            moveItemToIndex &&
              moveItemToIndex({
                item,
                targetRow,
                targetIndex,
                sourceRow,
                sourceIndex,
              });
          }
        }
      }
    },
    collect: (connect, monitor) => ({}),
  });

  const rowIsEmpty = items.length === 0;

  const renderErrorMessage = (field) =>
    errorMessagesFor(field).map((item) => (
      <li className={s.errorMessage}>{item}</li>
    ));

  const cardSource = {
    beginDrag(props) {
      return {
        id: props.id,
        index: props.index,
      };
    },
  };

  const updateValue = (data) => {
    if (updateValues) {
      updateValues(data);
    }
  };

  return (
    <div
      className={classnames(s.main, "columns", {
        [s.alignCenter]: rowIsEmpty,
      })}
      ref={drop}
    >
      {Object.keys(items).forEach((key) =>
        items[key] === undefined ? delete items[key] : {}
      )}
      {items.map((item, index) => {
        let filledValue = null;

        if (rowValues && rowValues[index]) {
          if (item.dataType === "FILE") {
            filledValue = rowValues[index].files;
          } else {
            filledValue = rowValues[index].value;
          }
        }

        const control = createControl({
          id: item.id,
          subtype: item.dataType,
          configuration: item.configuration,
          markSelected: markSelectedConfiguration,
          isSelected: item.id === selectedId,
          dragMode,
          formCreatorMode,
          onChange: (d) => {
            updateValue({ ...d, configuration: item.configuration });
          },
          removable: formCreatorMode,
          onRemove: () => removeItem(index),
          rowIndex,
          index,
          setIsDragging: setIsDragging,
          filledValue,
          filledValuesDetails,
          alertDisplaying,
          textColorFilling,
          filledFormMode,
          disabled,
          blankedRequiredFields,
          blankedRequiredArrayValueFields,
          checkBlankedFields,
          incorrectFileTypeIDs,
          setIncorrectFileTypeIDs,
          displayMode,
        });
        return (
          <React.Fragment key={index}>
            {showDropMarkers && index === 0 && (
              <ItemSeparator
                displayMode={displayMode}
                rowIndex={rowIndex}
                index={index}
                dragMode={dragMode}
                moveItemToIndex={moveItemToIndex}
              />
            )}
            <div className="column">
              {control}
              <span className={classes.hintText}>
                {item.configuration.required && "* Pole wymagane "}
              </span>
              <ul style={{ marginTop: "5px" }}>{renderErrorMessage(item)}</ul>
              {filledFormMode &&
                item.configuration.noteEnabled &&
                filledValuesDetails?.find((f) => f.id === item.id)?.note && (
                  <>
                    <hr />
                    <CssTextField
                      id="notes"
                      size="small"
                      label="Dodatkowa uwaga"
                      disabled={!edit}
                      value={
                        filledValuesDetails?.find((f) => f.id === item.id)
                          .note || ""
                      }
                      variant={
                        globalTheme === "high-contrast"
                          ? "outlined"
                          : "standard"
                      }
                      fullWidth
                    />
                  </>
                )}
              {!filledFormMode &&
                !formCreatorMode &&
                item.configuration.noteEnabled && (
                  <>
                    <hr
                      style={{
                        backgroundColor:
                          globalTheme === "high-contrast" && "yellow",
                      }}
                    />
                    <a
                      className={
                        globalTheme === "high-contrast" &&
                        classes.aalertHighContrast
                      }
                      onClick={() => onNoteUpdate({ id: item.id })}
                    >
                      {notes?.hasOwnProperty(item.id)
                        ? `Usuń uwagę`
                        : `Dodaj uwagę`}
                    </a>
                    {globalTheme === "high-contrast" && (
                      <>
                        <br /> <br />
                      </>
                    )}
                    {notes?.hasOwnProperty(item.id) && (
                      <CssTextField
                        id="notes"
                        size="small"
                        label="Dodatkowa uwaga"
                        value={notes[item.id] || ""}
                        fullWidth
                        variant={
                          globalTheme === "high-contrast"
                            ? "outlined"
                            : "standard"
                        }
                        onChange={({ target: { value: note } }) => {
                          onNoteUpdate({ note, id: item.id });
                        }}
                        // style={{
                        //   border: "1px solid lightgray",
                        //   borderRadius: "4px",
                        //   padding: "3px",
                        //   backgroundColor: "white",
                        // }}
                      />
                    )}
                  </>
                )}
            </div>
            {showDropMarkers && (
              <ItemSeparator
                displayMode={displayMode}
                rowIndex={rowIndex}
                index={index + 1}
                dragMode={dragMode}
                moveItemToIndex={moveItemToIndex}
              />
            )}
          </React.Fragment>
        );
      })}
      {rowIsEmpty && !(rowsLength - 1 === rowIndex) && (
        <div className="column">
          <ItemRowSeparator displayMode={displayMode} dragMode={dragMode} />
        </div>
      )}
      {!displayMode &&
        dragMode !== "none" &&
        rowIsEmpty &&
        rowsLength - 1 === rowIndex && (
          <div className="column" style={{ textAlign: "center" }}>
            Przeciągnij i upuść elementy formularza
          </div>
        )}
    </div>
  );
};

export default RowControl;
